import {
  createBrowserLocalThemePreferenceProvider,
} from "./theme-local-provider.js";

export const THEME_PREFERENCE_SCHEMA_VERSION = 1;
export const THEME_BOOT_CACHE_VERSION = 1;
export const THEME_NAMED_SELECTIONS = Object.freeze([
  "fishy",
  "dark",
  "abyss",
  "light",
  "silk",
  "cupcake",
  "caramellatte",
]);

const MAX_STORED_CHARS = 2048;
const DOCUMENT_FIELDS = Object.freeze([
  "revision",
  "schemaVersion",
  "selection",
]);
const SYSTEM_SELECTION_FIELDS = Object.freeze(["kind"]);
const NAMED_SELECTION_FIELDS = Object.freeze(["kind", "name"]);
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.theme-preference-repository.v1",
);
const BROWSER_LOCAL_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-local-theme-preference-repository.v1",
);
const BROWSER_LOCAL_REPOSITORY_PROPERTY =
  "__fishystuffLocalThemePreferenceRepositoryV1";

const SYSTEM_SELECTION = Object.freeze({ kind: "system" });

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function exactFields(value, expected) {
  const fields = Object.keys(value).sort();
  return fields.length === expected.length
    && fields.every((field, index) => field === expected[index]);
}

function freezeSelection(selection) {
  return selection.kind === "system"
    ? SYSTEM_SELECTION
    : Object.freeze({ kind: "named", name: selection.name });
}

function freezeState(value) {
  const next = { ...value };
  if (next.selection) next.selection = freezeSelection(next.selection);
  if (next.legacyCleanup) {
    next.legacyCleanup = Object.freeze({ ...next.legacyCleanup });
  }
  return Object.freeze(next);
}

function unavailable(operation, reason) {
  return freezeState({ kind: "unavailable", operation, reason });
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function selectionsEqual(left, right) {
  return left?.kind === right?.kind
    && (left?.kind !== "named" || left.name === right.name);
}

function decodeSelection(value) {
  if (!isRecord(value)) {
    return { ok: false, reason: "selection-must-be-an-object" };
  }
  if (value.kind === "system") {
    if (!exactFields(value, SYSTEM_SELECTION_FIELDS)) {
      return { ok: false, reason: "system-selection-fields-must-match-v1" };
    }
    return { ok: true, selection: SYSTEM_SELECTION };
  }
  if (value.kind === "named") {
    if (!exactFields(value, NAMED_SELECTION_FIELDS)) {
      return { ok: false, reason: "named-selection-fields-must-match-v1" };
    }
    if (!THEME_NAMED_SELECTIONS.includes(value.name)) {
      return { ok: false, reason: "named-theme-is-not-supported" };
    }
    return {
      ok: true,
      selection: Object.freeze({ kind: "named", name: value.name }),
    };
  }
  return { ok: false, reason: "selection-kind-is-not-supported" };
}

function requireSelection(value) {
  const decoded = decodeSelection(value);
  if (!decoded.ok) {
    throw new TypeError(`Invalid theme selection: ${decoded.reason}.`);
  }
  return decoded.selection;
}

function expectedRevisionFrom(options) {
  if (options == null) return undefined;
  if (!isRecord(options)) {
    throw new TypeError("Theme command options must be an object.");
  }
  const fields = Object.keys(options);
  if (fields.length > 1 || fields.some((field) => field !== "expectedRevision")) {
    throw new TypeError("Theme command options contain unsupported fields.");
  }
  const revision = options.expectedRevision;
  if (revision === undefined || revision === null) return revision;
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError("Expected theme revision must be null or a positive safe integer.");
  }
  return revision;
}

export function decodeThemePreferenceDocument(raw) {
  if (typeof raw !== "string") {
    return freezeState({
      kind: "invalid",
      reason: "stored-value-must-be-a-string",
    });
  }
  if (raw.length > MAX_STORED_CHARS) {
    return freezeState({
      kind: "invalid",
      reason: "stored-value-exceeds-v1-limit",
    });
  }
  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return freezeState({ kind: "invalid", reason: "malformed-json" });
  }
  if (!isRecord(document)) {
    return freezeState({
      kind: "invalid",
      reason: "document-must-be-an-object",
    });
  }
  if (
    Number.isSafeInteger(document.schemaVersion)
    && document.schemaVersion > THEME_PREFERENCE_SCHEMA_VERSION
  ) {
    return freezeState({
      kind: "unsupportedVersion",
      foundVersion: document.schemaVersion,
    });
  }
  if (!exactFields(document, DOCUMENT_FIELDS)) {
    return freezeState({
      kind: "invalid",
      reason: "document-fields-must-match-v1",
    });
  }
  if (document.schemaVersion !== THEME_PREFERENCE_SCHEMA_VERSION) {
    return freezeState({
      kind: "invalid",
      reason: "schema-version-must-be-1",
    });
  }
  if (!Number.isSafeInteger(document.revision) || document.revision <= 0) {
    return freezeState({
      kind: "invalid",
      reason: "revision-must-be-a-positive-safe-integer",
    });
  }
  const selection = decodeSelection(document.selection);
  if (!selection.ok) {
    return freezeState({ kind: "invalid", reason: selection.reason });
  }
  return freezeState({
    kind: "ready",
    revision: document.revision,
    selection: selection.selection,
  });
}

export function encodeThemePreferenceDocument({ revision, selection }) {
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError("Theme revision must be a positive safe integer.");
  }
  return JSON.stringify({
    schemaVersion: THEME_PREFERENCE_SCHEMA_VERSION,
    revision,
    selection: requireSelection(selection),
  });
}

export function encodeThemeBootCache({ revision, selection }) {
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError("Theme cache source revision must be a positive safe integer.");
  }
  return JSON.stringify({
    cacheVersion: THEME_BOOT_CACHE_VERSION,
    sourceRevision: revision,
    selection: requireSelection(selection),
  });
}

export function themeSelectionName(selection) {
  const exact = requireSelection(selection);
  return exact.kind === "system" ? "system" : exact.name;
}

export function themePreferenceDecision(state) {
  const source = isRecord(state) ? state : { kind: "uninitialized" };
  if (source.kind === "ready") {
    return freezeState({
      repositoryKind: "ready",
      revision: source.revision,
      selection: source.selection,
      canChange: true,
    });
  }
  if (source.kind === "missing") {
    return freezeState({
      repositoryKind: "missing",
      revision: null,
      selection: SYSTEM_SELECTION,
      canChange: true,
    });
  }
  if (source.kind === "conflict") {
    return freezeState({
      repositoryKind: "conflict",
      revision: source.actualRevision,
      selection: source.selection,
      canChange: false,
    });
  }
  return freezeState({
    repositoryKind: typeof source.kind === "string"
      ? source.kind
      : "uninitialized",
    revision: null,
    selection: SYSTEM_SELECTION,
    canChange: false,
  });
}

function legacySelection(candidate) {
  if (typeof candidate.value !== "string") {
    return freezeState({
      kind: "invalid",
      reason: "legacy-theme-must-be-a-string",
      legacySource: candidate.source,
    });
  }
  if (candidate.value === "system") return SYSTEM_SELECTION;
  if (THEME_NAMED_SELECTIONS.includes(candidate.value)) {
    return Object.freeze({ kind: "named", name: candidate.value });
  }
  return freezeState({
    kind: "invalid",
    reason: "legacy-theme-is-not-supported",
    legacySource: candidate.source,
  });
}

export function createThemePreferenceRepository({ provider = null } = {}) {
  if (
    !provider
    || typeof provider.dispose !== "function"
    || typeof provider.readDocument !== "function"
    || typeof provider.readLegacySelection !== "function"
    || typeof provider.removeDocument !== "function"
    || typeof provider.removeLegacySelections !== "function"
    || typeof provider.subscribeInvalidation !== "function"
    || typeof provider.writeDocument !== "function"
  ) {
    throw new TypeError("Theme repository requires the exact local provider contract.");
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();

  function state() {
    return freezeState(currentState);
  }

  function decision() {
    return themePreferenceDecision(currentState);
  }

  function deliver(listener) {
    try {
      listener(state());
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState) {
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return state();
    currentState = frozen;
    if (!disposed) {
      for (const listener of listeners) deliver(listener);
    }
    return state();
  }

  function readAuthoritativeState() {
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable(stored.operation, stored.reason);
    }
    return stored.raw === null
      ? freezeState({ kind: "missing" })
      : decodeThemePreferenceDocument(stored.raw);
  }

  function withLegacyCleanupState(authoritative) {
    const cleanup = provider.removeLegacySelections();
    if (cleanup.kind !== "unavailable") return authoritative;
    return freezeState({
      ...authoritative,
      legacyCleanup: {
        kind: "unavailable",
        operation: "remove",
        reason: cleanup.reason,
      },
    });
  }

  function writeReady(revision, selection) {
    const raw = encodeThemePreferenceDocument({ revision, selection });
    const written = provider.writeDocument(raw);
    if (written.kind === "unavailable") {
      return publish(unavailable(written.operation, written.reason));
    }
    const ready = withLegacyCleanupState(
      freezeState({ kind: "ready", revision, selection }),
    );
    // The verified v1 document is authoritative before either retired source
    // is touched. Cleanup is deletion-only and retried on every ready load.
    return publish(ready);
  }

  function load() {
    const authoritative = readAuthoritativeState();
    if (authoritative.kind === "ready") {
      return publish(withLegacyCleanupState(authoritative));
    }
    if (authoritative.kind !== "missing") {
      return publish(authoritative);
    }

    const legacy = provider.readLegacySelection();
    if (legacy.kind === "unavailable") {
      return publish(unavailable(legacy.operation, legacy.reason));
    }
    if (legacy.kind === "missing") {
      return publish(authoritative);
    }
    const imported = legacySelection(legacy);
    if (imported.kind === "invalid") {
      return publish(imported);
    }
    return writeReady(1, imported);
  }

  function conflict(expectedRevision, authoritative) {
    return publish({
      kind: "conflict",
      expectedRevision,
      actualRevision: authoritative.kind === "ready"
        ? authoritative.revision
        : null,
      selection: authoritative.kind === "ready"
        ? authoritative.selection
        : SYSTEM_SELECTION,
    });
  }

  async function setSelection(selection, options = {}) {
    const exactSelection = requireSelection(selection);
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();
    if (["invalid", "unsupportedVersion", "unavailable"].includes(authoritative.kind)) {
      return publish(authoritative);
    }
    if (authoritative.kind === "missing") {
      if (expectedRevision !== undefined && expectedRevision !== null) {
        return conflict(expectedRevision, authoritative);
      }
      return writeReady(1, exactSelection);
    }
    if (
      expectedRevision !== undefined
      && expectedRevision !== authoritative.revision
    ) {
      if (selectionsEqual(authoritative.selection, exactSelection)) {
        return publish(authoritative);
      }
      return conflict(expectedRevision, authoritative);
    }
    if (selectionsEqual(authoritative.selection, exactSelection)) {
      return publish(authoritative);
    }
    if (authoritative.revision >= Number.MAX_SAFE_INTEGER) {
      return publish(unavailable("write", "revision-exhausted"));
    }
    return writeReady(authoritative.revision + 1, exactSelection);
  }

  function selectNamedTheme(name, options = {}) {
    return setSelection({ kind: "named", name }, options);
  }

  function followSystemTheme(options = {}) {
    return setSelection(SYSTEM_SELECTION, options);
  }

  async function exportRecord() {
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable("export", stored.reason);
    }
    if (stored.raw !== null) {
      const decoded = decodeThemePreferenceDocument(stored.raw);
      return freezeState({
        kind: "exported",
        recordKind: decoded.kind,
        raw: stored.raw,
      });
    }
    const legacy = provider.readLegacySelection();
    if (legacy.kind === "unavailable") {
      return unavailable("export", legacy.reason);
    }
    if (legacy.kind === "missing") return freezeState({ kind: "missing" });
    return freezeState({
      kind: "exported",
      recordKind: legacySelection(legacy).kind === "invalid"
        ? "legacyInvalid"
        : "legacy",
      legacySource: legacy.source,
      raw: legacy.raw,
    });
  }

  async function reset(options = {}) {
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();
    if (authoritative.kind === "unavailable") return publish(authoritative);
    if (
      authoritative.kind === "ready"
      && expectedRevision !== undefined
      && expectedRevision !== authoritative.revision
    ) {
      return conflict(expectedRevision, authoritative);
    }
    if (
      authoritative.kind === "missing"
      && expectedRevision !== undefined
      && expectedRevision !== null
    ) {
      return conflict(expectedRevision, authoritative);
    }

    const legacy = provider.removeLegacySelections();
    if (legacy.kind === "unavailable") {
      return publish(unavailable("remove", legacy.reason));
    }
    if (authoritative.kind !== "missing") {
      const removed = provider.removeDocument();
      if (removed.kind === "unavailable") {
        return publish(unavailable(removed.operation, removed.reason));
      }
    }
    return publish({ kind: "missing" });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Theme repository listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeInvalidation = provider.subscribeInvalidation(() => load());

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribeInvalidation();
    listeners.clear();
    provider.dispose();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: THEME_PREFERENCE_SCHEMA_VERSION,
    decision,
    dispose,
    exportRecord,
    followSystemTheme,
    load,
    reset,
    selectNamedTheme,
    setSelection,
    state,
    subscribe,
  });
}

function compatibleBrowserRepository(value) {
  return Boolean(
    value
    && value[BROWSER_LOCAL_REPOSITORY_BRAND]
      === THEME_PREFERENCE_SCHEMA_VERSION
    && typeof value.decision === "function"
    && typeof value.exportRecord === "function"
    && typeof value.followSystemTheme === "function"
    && typeof value.load === "function"
    && typeof value.reset === "function"
    && typeof value.selectNamedTheme === "function"
    && typeof value.setSelection === "function"
    && typeof value.state === "function"
    && typeof value.subscribe === "function",
  );
}

function browserRepositoryView(repository) {
  return Object.freeze({
    [BROWSER_LOCAL_REPOSITORY_BRAND]: THEME_PREFERENCE_SCHEMA_VERSION,
    decision: repository.decision,
    exportRecord: repository.exportRecord,
    followSystemTheme: repository.followSystemTheme,
    load: repository.load,
    reset: repository.reset,
    selectNamedTheme: repository.selectNamedTheme,
    setSelection: repository.setSelection,
    state: repository.state,
    subscribe: repository.subscribe,
  });
}

let browserLocalRepository = null;

export function browserLocalThemePreferenceRepository() {
  const browser = globalThis.window ?? globalThis;
  const shared = browser?.[BROWSER_LOCAL_REPOSITORY_PROPERTY];
  if (shared !== undefined) {
    if (!compatibleBrowserRepository(shared)) {
      throw new TypeError(
        "The shared theme preference repository has an incompatible contract.",
      );
    }
    browserLocalRepository = shared;
  }
  if (!browserLocalRepository) {
    const repository = createThemePreferenceRepository({
      provider: createBrowserLocalThemePreferenceProvider(),
    });
    repository.load();
    browserLocalRepository = browserRepositoryView(repository);
  }
  if (browser && (typeof browser === "object" || typeof browser === "function")) {
    Object.defineProperty(browser, BROWSER_LOCAL_REPOSITORY_PROPERTY, {
      configurable: true,
      value: browserLocalRepository,
      writable: false,
    });
  }
  return browserLocalRepository;
}
